import React from 'react';
import {INav, INavState} from 'features/layout/redux/helpers/FoldersHelper';
import {without} from 'lodash';
import {TreeExpandCollapseContext} from '../treeExpandCollapseContext';
import {useEnsureRootFoldersVisible} from 'features/layout/SideNav/hooks/useEnsureRootFoldersVisible';
import {useEnsureCurrentNodeVisible} from 'features/layout/SideNav/hooks/useEnsureCurrentNodeVisible';
import {infiniteListClassName, infiniteItemHeight} from 'features/layout/SideNav/constant';
import {flatThroughTree} from 'features/layout/redux/helpers/utils';

export const useBuildTreeExpandCollapseContext = (nodes: INav[]) => {
   const [expanded, setExpanded] = React.useState<string[]>([]);

   const expandedRef = React.useRef<string[]>(expanded);
   React.useEffect(() => {
      expandedRef.current = expanded;
   }, [expanded]);

   const concatExpanded = React.useCallback(
      (nodes: string[]) => {
         setExpanded([...expandedRef.current, ...nodes]);
      },
      [expandedRef, setExpanded]
   );

   const [flattened, setFlattened] = React.useState<INavState[]>([]);
   React.useEffect(() => {
      const f = [] as INavState[];
      flatThroughTree(f, nodes, 0, expanded);
      setFlattened(f);
      // console.debug(expanded, f, nodes);
   }, [nodes, setFlattened, expanded]);

   const toggleNode = React.useCallback(
      (nodeId: string) => {
         const i = expanded.indexOf(nodeId);
         // console.debug(expanded, nodeId, i);
         if (i < 0) {
            setExpanded([...expanded, nodeId]);
         } else {
            setExpanded(without(expanded, nodeId));
         }
      },
      [expanded, setExpanded]
   );

   useEnsureRootFoldersVisible(nodes, setExpanded);
   useEnsureCurrentNodeVisible(nodes, concatExpanded, flattened);

   const contextValue = React.useMemo(
      () =>
         ({
            flattened,
            expanded,
            setExpanded,
            toggleNode,
         } as TreeExpandCollapseContext),
      [flattened, expanded, setExpanded, toggleNode]
   );

   return {
      infiniteListClassName,
      infiniteItemHeight,
      flattened,
      contextValue,
   };
};
